import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import useCustomer from '../../hooks/useCustomer';
import Card from '../../common/Card';
import Button from '../../common/Button';
import FormInput from '../../common/FormInput';
import FormSelect from '../../common/FormSelect';
import FormCheckbox from '../../common/FormCheckbox';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import { getCsrfToken } from '../../../utils/csrfUtils';
import './styles.css';

/**
 * 客戶表單組件
 * 用於創建或編輯客戶信息
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @param {Function} props.onSuccess - 成功提交後的回調函數
 * @returns {JSX.Element} 客戶表單組件
 */
function CustomerForm({ token, onSuccess }) {
    const navigate = useNavigate();
    const { customerId } = useParams(); // 從URL參數中獲取customerId

    // 使用自定義Hook
    const {
        customers,
        loading,
        error,
        selectedCustomer,
        customerLoading,
        customerError,
        getCustomerById
    } = useCustomer(token);

    // 本地狀態
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        nfc_id: '',
        level: 'Regular',
        gender: '',
        receiveRelatedInfo: false
    });

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [isEditMode, setIsEditMode] = useState(!!customerId);

    // 加載客戶數據（編輯模式）
    useEffect(() => {
        if (isEditMode && customerId) {

            const loadCustomer = async () => {

                const customerData = await getCustomerById(customerId);
                if (customerData) {
                    // console.log('載入客戶資料:', customerData);

                    // 處理性別數據，確保正確格式
                    let genderValue = '';
                    if (customerData.gender) {
                        // 將性別轉換為小寫以進行比較
                        const genderLower = customerData.gender.toLowerCase();
                        if (genderLower === 'male' || genderLower === 'female' || genderLower === 'other') {
                            genderValue = genderLower;
                        } else {
                            genderValue = 'other'; // 默認為其他
                        }
                    }

                    // 處理接收相關資訊偏好設定
                    const receiveInfo =
                        customerData.receiveRelatedInfo === true ||
                        customerData.receiveRelatedInfo === 'true' ||
                        customerData.receiveRelatedInfo === 'Yes' ||
                        customerData.receive_related_information === 'Yes' ||
                        customerData.receive_related_information === true;

                    // console.log('處理後的偏好設定:', {
                    //     gender: genderValue,
                    //     receiveRelatedInfo: receiveInfo
                    // });

                    setFormData({
                        firstName: customerData.firstName || '',
                        lastName: customerData.lastName || '',
                        email: customerData.email || '',
                        phone: customerData.phone || '',
                        nfc_id: customerData.nfc_id || '',
                        level: customerData.level || 'Regular',
                        gender: genderValue,
                        receiveRelatedInfo: receiveInfo
                    });
                }
            };

            loadCustomer();
        }
    }, [isEditMode, customerId, getCustomerById]);

    // 處理輸入變更
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // 處理表單提交
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveError(null);

        try {
            // 驗證表單數據
            if (!formData.firstName || !formData.lastName || !formData.email) {
                throw new Error('姓名和電子郵件為必填項');
            }

            // 準備客戶數據
            const customerData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                nfc_id: formData.nfc_id,
                level: formData.level,
                gender: formData.gender,
                receiveRelatedInfo: formData.receiveRelatedInfo
            };

            // 獲取CSRF令牌
            const csrfToken = getCsrfToken();

            // 創建或更新客戶
            let response;

            if (isEditMode) {
                // 更新現有客戶
                response = await fetch(`/api/customers/${customerId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'X-CSRF-Token': csrfToken
                    },
                    body: JSON.stringify(customerData)
                });
            } else {
                // 創建新客戶
                response = await fetch('/api/customers', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'X-CSRF-Token': csrfToken
                    },
                    body: JSON.stringify(customerData)
                });
            }

            if (!response.ok) {
                throw new Error(`保存客戶失敗: ${response.status} ${response.statusText}`);
            }

            const responseData = await response.json();

            // 處理成功
            if (onSuccess) {
                onSuccess(responseData);
            } else {
                navigate(isEditMode ? `/customers/${customerId}` : '/customers');
            }
        } catch (error) {
            console.error('保存客戶時出錯:', error);
            setSaveError(error.message || '保存客戶時出錯');
        } finally {
            setSaving(false);
        }
    };

    // 處理取消
    const handleCancel = () => {
        if (onSuccess) {
            onSuccess();
        } else {
            navigate(isEditMode ? `/customers/${customerId}` : '/customers');
        }
    };

    // 加載中狀態
    if ((isEditMode && customerLoading)) {
        return <Loading text="正在加載客戶數據..." />;
    }

    // 錯誤狀態
    if ((isEditMode && customerError)) {
        return (
            <ErrorMessage
                message={customerError}
                onRetry={() => window.location.reload()}
            />
        );
    }

    return (
        <div className="customer-form-container">
            <Card
                title={isEditMode ? '編輯客戶資料' : '新增客戶'}
                className="customer-form-card"
            >
                {saveError && <ErrorMessage message={saveError} />}

                <form onSubmit={handleSubmit} className="customer-form">
                    <div className="customer-form-section">
                        <h3 className="customer-form-section-title">基本資料</h3>

                        <div className="customer-form-row">
                            <FormInput
                                id="firstName"
                                name="firstName"
                                label="名字"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                placeholder="請輸入名字"
                            />

                            <FormInput
                                id="lastName"
                                name="lastName"
                                label="姓氏"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                                placeholder="請輸入姓氏"
                            />
                        </div>

                        <div className="customer-form-row">
                            <FormInput
                                id="email"
                                name="email"
                                type="email"
                                label="電子郵件"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                placeholder="請輸入電子郵件"
                            />

                            <FormInput
                                id="phone"
                                name="phone"
                                label="電話"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="請輸入電話號碼"
                            />
                        </div>

                        <div className="customer-form-row">
                            <FormInput
                                id="nfc_id"
                                name="nfc_id"
                                label="NFC ID"
                                value={formData.nfc_id}
                                onChange={handleInputChange}
                                placeholder="請輸入NFC ID"
                            />

                            <FormSelect
                                id="level"
                                name="level"
                                label="客戶等級"
                                value={formData.level}
                                onChange={handleInputChange}
                                options={[
                                    { value: 'Regular', label: '普通' },
                                    { value: 'VIP', label: 'VIP' },
                                    { value: 'Premium VIP', label: '高級VIP' }
                                ]}
                            />
                        </div>
                    </div>

                    <div className="customer-form-section">
                        <h3 className="customer-form-section-title">偏好設定</h3>

                        <div className="customer-form-row">
                            <FormSelect
                                id="gender"
                                name="gender"
                                label="性別"
                                value={formData.gender}
                                onChange={handleInputChange}
                                options={[
                                    { value: '', label: '請選擇' },
                                    { value: 'male', label: '男性' },
                                    { value: 'female', label: '女性' },
                                    { value: 'other', label: '其他' }
                                ]}
                            />
                        </div>

                        <div className="customer-form-row">
                            <FormCheckbox
                                id="receiveRelatedInfo"
                                name="receiveRelatedInfo"
                                label="接收相關資訊和優惠"
                                checked={formData.receiveRelatedInfo}
                                onChange={handleInputChange}
                                helpText="勾選此選項表示您同意接收我們的最新消息、優惠和相關資訊。"
                            />
                        </div>
                    </div>

                    <div className="customer-form-actions">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                        >
                            取消
                        </Button>

                        <Button
                            type="submit"
                            variant="primary"
                            disabled={saving}
                        >
                            {saving ? '保存中...' : (isEditMode ? '更新客戶' : '創建客戶')}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}

export default CustomerForm; 